import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { sessionService, SessionDetails } from "../../services/sessionService";
import { SessionPage } from "../../pages/SessionPage";
import { apiFetch } from "../../services/apiClient";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Select, LoadingState } from "../ui";

interface CampaignCharacter {
  id: number;
  name: string;
  campaignId: number;
  userId: number;
}

// Wrapper component to extract route params and pass to SessionPage
export const SessionPageWrapper: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const userId = auth.get("userId") || "";
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Join-flow state
  const [joinPhase, setJoinPhase] = useState<"idle" | "joining" | "selecting" | "done">("idle");
  const [characters, setCharacters] = useState<CampaignCharacter[]>([]);
  const [selectedCharacterId, setSelectedCharacterId] = useState<string>("");
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joiningInProgress, setJoiningInProgress] = useState(false);

  const performJoin = useCallback(
    async (details: SessionDetails, characterId: number | null) => {
      setJoiningInProgress(true);
      setJoinError(null);
      try {
        const res = await apiFetch("/api/protected/sessions/join", {
          method: "POST",
          body: JSON.stringify({
            session_id: details.session_id,
            character_id: characterId,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error((data as { message?: string }).message || "Failed to join session");
        }
        // Re-fetch session details so characterId / characterName are populated
        const token = auth.token();
        if (!token) throw new Error("Authentication required");
        const refreshed = await sessionService.getSessionDetails(String(details.session_id));
        setSessionDetails(refreshed);
        setJoinPhase("done");
      } catch (err) {
        setJoinError(err instanceof Error ? err.message : "Failed to join session");
      } finally {
        setJoiningInProgress(false);
      }
    },
    [auth]
  );

  useEffect(() => {
    const fetchAndJoin = async () => {
      if (!sessionId) {
        setError("Session ID not found");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = auth.token();
        if (!token) throw new Error("Authentication required");

        const details = await sessionService.getSessionDetails(sessionId);
        console.log("Session details fetched successfully:", JSON.stringify(details));

        // Already participating — go straight to session page
        if (details.is_participating) {
          setSessionDetails(details);
          setJoinPhase("done");
          setLoading(false);
          return;
        }

        // Session must be active to join
        if (details.status !== "active") {
          setSessionDetails(details);
          setJoinPhase("done");
          setLoading(false);
          return;
        }

        const currentUserId = parseInt(userId, 10);

        // DM joins without a character
        if (details.is_dm || details.dm_player_id === currentUserId) {
          setLoading(false);
          setJoinPhase("joining");
          await performJoin(details, null);
          return;
        }

        // Fetch characters for this campaign owned by the current user
        const charRes = await apiFetch(
          `/api/protected/campaigns/campaign/${details.campaign_id}/characters`
        );
        let myChars: CampaignCharacter[] = [];
        if (charRes.ok) {
          const all: CampaignCharacter[] = await charRes.json();
          myChars = all.filter((c) => c.userId === currentUserId);
        }

        if (myChars.length === 0) {
          // No characters — cannot join
          setSessionDetails(details);
          setError(
            "You have no characters in this campaign. Create a character first to join the session."
          );
          setLoading(false);
          return;
        }

        if (myChars.length === 1) {
          // Auto-join with the only character
          setLoading(false);
          setJoinPhase("joining");
          await performJoin(details, myChars[0].id);
          return;
        }

        // Multiple characters — show modal
        setCharacters(myChars);
        setSessionDetails(details);
        setJoinPhase("selecting");
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch session details:", err);
        setError(err instanceof Error ? err.message : "Failed to load session");
        setLoading(false);
      }
    };

    fetchAndJoin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleModalJoin = async () => {
    if (!sessionDetails || !selectedCharacterId || joiningInProgress) return;
    await performJoin(sessionDetails, parseInt(selectedCharacterId, 10));
  };

  // --- Render states ---

  if (!sessionId) {
    return <div className="p-4 text-center">Session ID not found</div>;
  }

  if (loading || joinPhase === "joining") {
    return (
      <div className="p-4 flex items-center justify-center min-h-screen">
        <LoadingState message="Loading session..." size="lg" variant="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-400">
        <h2 className="text-xl font-bold mb-2">Cannot Join Session</h2>
        <p>{error}</p>
        <Button
          className="mt-4"
          variant="secondary"
          onClick={() => navigate("/protected/characters")}
        >
          Go to Characters
        </Button>
      </div>
    );
  }

  if (!sessionDetails) {
    return <div className="p-4 text-center">Session not found</div>;
  }

  // Character selection modal
  if (joinPhase === "selecting") {
    return (
      <div className="p-4 flex items-center justify-center min-h-screen bg-zinc-950">
        <Modal isOpen onClose={() => navigate(-1)} size="md">
          <ModalHeader title="Select Your Character" onClose={() => navigate(-1)} />
          <ModalBody>
            <div className="space-y-4">
              <p className="text-zinc-300">
                You have multiple characters in this campaign. Select one to join the session with.
              </p>
              <Select
                label="Character"
                value={selectedCharacterId}
                onChange={(e) => setSelectedCharacterId(e.target.value)}
                options={[
                  { value: "", label: "Select a character" },
                  ...characters.map((c) => ({ value: c.id.toString(), label: c.name })),
                ]}
                fullWidth
                required
              />
              {joinError && (
                <div className="bg-red-900/20 border border-red-800 text-red-300 px-4 py-3">
                  {joinError}
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => navigate(-1)} variant="secondary" size="md">
              Cancel
            </Button>
            <Button
              onClick={handleModalJoin}
              variant="primary"
              size="md"
              disabled={!selectedCharacterId || joiningInProgress}
            >
              {joiningInProgress ? "Joining..." : "Join Session"}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }

  return (
    <SessionPage
      sessionId={sessionId}
      userId={String(userId)}
      characterId={sessionDetails.character_id?.toString()}
      characterName={sessionDetails.character_name ?? undefined}
      isDM={sessionDetails.is_dm}
      campaignId={sessionDetails.campaign_id}
      campaignCreatedBy={sessionDetails.campaign_created_by}
    />
  );
};
